import { ApiError, post } from '../api';
import { storage } from '../storage';

/**
 * AuthProvider implementations (frontend side).
 * - LocalAuthClient: email/password against our API (development and small deployments).
 * - SupabaseAuthClient: Supabase Auth REST endpoints ONLY (no supabase-js, no Realtime).
 *   The API verifies the Supabase JWT and maps it to our users table.
 */
export interface AuthClient {
  readonly kind: 'local' | 'supabase';
  getToken(): Promise<string | null>;
  login(email: string, password: string): Promise<void>;
  register(input: { fullName: string; email: string; password: string; phone?: string; language: 'fr' | 'en' }): Promise<void>;
  devLogin(email: string): Promise<void>;
  logout(): Promise<void>;
  /** Sends a reset link if the account exists. Always resolves the same way (no enumeration). */
  requestPasswordReset(email: string): Promise<void>;
  /** Which reset flow the current URL carries: local `?token=` or a Supabase recovery link. */
  resetMode(search: URLSearchParams): 'local' | 'supabase' | null;
  /** Completes a reset (local token or Supabase recovery session). */
  resetPassword(input: { password: string; token?: string | null }): Promise<void>;
  /** Logged-in change; the current password is always verified. */
  changePassword(currentPassword: string, newPassword: string): Promise<void>;
}

const TOKEN_KEY = 'menage.token';

export class LocalAuthClient implements AuthClient {
  readonly kind = 'local' as const;

  async getToken() {
    return storage.get(TOKEN_KEY);
  }

  async login(email: string, password: string) {
    const r = await post<{ token: string }>('/auth/login', { email, password });
    storage.set(TOKEN_KEY, r.token);
  }

  async register(input: { fullName: string; email: string; password: string; phone?: string; language: 'fr' | 'en' }) {
    const r = await post<{ token: string }>('/auth/register', input);
    storage.set(TOKEN_KEY, r.token);
  }

  async devLogin(email: string) {
    const r = await post<{ token: string }>('/auth/dev-login', { email });
    storage.set(TOKEN_KEY, r.token);
  }

  async logout() {
    storage.remove(TOKEN_KEY);
  }

  async requestPasswordReset(email: string) {
    await post('/auth/password/forgot', { email });
  }

  resetMode(search: URLSearchParams) {
    return search.get('token') ? ('local' as const) : null;
  }

  async resetPassword(input: { password: string; token?: string | null }) {
    if (!input.token) throw new ApiError(400, 'INVALID_RESET_TOKEN');
    await post('/auth/password/reset', { token: input.token, password: input.password });
  }

  async changePassword(currentPassword: string, newPassword: string) {
    await post('/auth/password/change', { currentPassword, newPassword });
  }
}

interface SupabaseSession {
  access_token: string;
  refresh_token: string;
  expires_at: number;
}

export class SupabaseAuthClient implements AuthClient {
  readonly kind = 'supabase' as const;
  private static readonly KEY = 'menage.supabase.session';
  /** Recovery session from a reset link: kept apart (this tab only) so it never acts as a login. */
  private static readonly RECOVERY_KEY = 'menage.supabase.recovery';

  constructor(
    private readonly url: string,
    private readonly anonKey: string,
  ) {
    this.consumeRedirect();
  }

  /** Email confirmation / magic links land on the app with the session in the URL hash. */
  private consumeRedirect() {
    if (typeof window === 'undefined' || !window.location.hash.includes('access_token=')) return;
    const h = new URLSearchParams(window.location.hash.slice(1));
    const access = h.get('access_token');
    const refresh = h.get('refresh_token');
    if (access && h.get('type') === 'recovery') {
      const expiresAt = Number(h.get('expires_at')) || Math.floor(Date.now() / 1000) + Number(h.get('expires_in') ?? 3600);
      try {
        window.sessionStorage.setItem(SupabaseAuthClient.RECOVERY_KEY, JSON.stringify({ access_token: access, expires_at: expiresAt }));
      } catch {
        /* storage blocked: the reset page will ask for a new link */
      }
    } else if (access && refresh) {
      const expiresAt = Number(h.get('expires_at')) || Math.floor(Date.now() / 1000) + Number(h.get('expires_in') ?? 3600);
      storage.set(SupabaseAuthClient.KEY, JSON.stringify({ access_token: access, refresh_token: refresh, expires_at: expiresAt }));
    }
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }

  private session(): SupabaseSession | null {
    const raw = storage.get(SupabaseAuthClient.KEY);
    try {
      return raw ? (JSON.parse(raw) as SupabaseSession) : null;
    } catch {
      return null;
    }
  }

  private async call(path: string, body: unknown): Promise<SupabaseSession> {
    const res = await fetch(`${this.url}/auth/v1/${path}`, {
      method: 'POST',
      headers: { apikey: this.anonKey, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new ApiError(401, 'INVALID_CREDENTIALS');
    const s = (await res.json()) as SupabaseSession & { expires_in?: number };
    const session = { ...s, expires_at: s.expires_at ?? Math.floor(Date.now() / 1000) + (s.expires_in ?? 3600) };
    storage.set(SupabaseAuthClient.KEY, JSON.stringify(session));
    return session;
  }

  async getToken() {
    const s = this.session();
    if (!s) return null;
    if (s.expires_at - 60 > Date.now() / 1000) return s.access_token;
    try {
      return (await this.call('token?grant_type=refresh_token', { refresh_token: s.refresh_token })).access_token;
    } catch {
      storage.remove(SupabaseAuthClient.KEY);
      return null;
    }
  }

  async login(email: string, password: string) {
    await this.call('token?grant_type=password', { email, password });
  }

  async register(input: { fullName: string; email: string; password: string }) {
    const redirect = encodeURIComponent(`${window.location.origin}/app`);
    const res = await fetch(`${this.url}/auth/v1/signup?redirect_to=${redirect}`, {
      method: 'POST',
      headers: { apikey: this.anonKey, 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: input.email, password: input.password, data: { full_name: input.fullName } }),
    });
    if (!res.ok) throw new ApiError(res.status, res.status === 422 ? 'EMAIL_TAKEN' : 'INTERNAL');
    const body = (await res.json()) as Partial<SupabaseSession> & { expires_in?: number };
    // Email confirmation enabled: no session until the link in the email is clicked.
    if (!body.access_token) throw new ApiError(0, 'CONFIRM_EMAIL');
    storage.set(SupabaseAuthClient.KEY, JSON.stringify({ ...body, expires_at: body.expires_at ?? Math.floor(Date.now() / 1000) + (body.expires_in ?? 3600) }));
    await post('/auth/provision', { fullName: input.fullName });
  }

  async devLogin() {
    throw new Error('dev_login_unavailable');
  }

  async logout() {
    storage.remove(SupabaseAuthClient.KEY);
  }

  async requestPasswordReset(email: string) {
    const redirect = encodeURIComponent(`${window.location.origin}/reinitialiser-mot-de-passe`);
    try {
      await fetch(`${this.url}/auth/v1/recover?redirect_to=${redirect}`, {
        method: 'POST',
        headers: { apikey: this.anonKey, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
    } catch {
      throw new ApiError(0, 'NETWORK');
    }
    // Any HTTP answer is reported as the same neutral success (no account enumeration).
  }

  private recovery(): { access_token: string; expires_at: number } | null {
    try {
      const raw = window.sessionStorage.getItem(SupabaseAuthClient.RECOVERY_KEY);
      const r = raw ? (JSON.parse(raw) as { access_token: string; expires_at: number }) : null;
      return r && r.expires_at > Date.now() / 1000 ? r : null;
    } catch {
      return null;
    }
  }

  resetMode() {
    return this.recovery() ? ('supabase' as const) : null;
  }

  /** PUT /auth/v1/user, mapping GoTrue error codes to our stable codes. */
  private async updatePassword(accessToken: string, password: string, invalidCode: string) {
    let res: Response;
    try {
      res = await fetch(`${this.url}/auth/v1/user`, {
        method: 'PUT',
        headers: { apikey: this.anonKey, Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
    } catch {
      throw new ApiError(0, 'NETWORK');
    }
    if (res.ok) return;
    const body = (await res.json().catch(() => ({}))) as { error_code?: string; code?: string | number };
    const code = String(body.error_code ?? body.code ?? '');
    if (code === 'same_password') throw new ApiError(422, 'SAME_PASSWORD');
    if (code === 'weak_password') throw new ApiError(422, 'WEAK_PASSWORD');
    if (code === 'reauthentication_needed' || code === 'reauthentication_not_valid') throw new ApiError(403, 'REAUTH_REQUIRED');
    if (res.status === 401 || res.status === 403) throw new ApiError(400, invalidCode);
    if (res.status === 429) throw new ApiError(429, 'RATE_LIMITED');
    throw new ApiError(res.status, 'INTERNAL');
  }

  async resetPassword(input: { password: string }) {
    const r = this.recovery();
    if (!r) throw new ApiError(400, 'INVALID_RESET_TOKEN');
    await this.updatePassword(r.access_token, input.password, 'INVALID_RESET_TOKEN');
    try {
      window.sessionStorage.removeItem(SupabaseAuthClient.RECOVERY_KEY);
    } catch {
      /* ignore */
    }
  }

  async changePassword(currentPassword: string, newPassword: string) {
    const token = await this.getToken();
    const email = token ? jwtEmail(token) : null;
    if (!token || !email) throw new ApiError(401, 'UNAUTHORIZED');
    // Signing in again with the current password verifies it AND yields a fresh session,
    // which satisfies Supabase "secure password change" (recent login required).
    let fresh: SupabaseSession;
    try {
      fresh = await this.call('token?grant_type=password', { email, password: currentPassword });
    } catch {
      throw new ApiError(400, 'WRONG_PASSWORD');
    }
    await this.updatePassword(fresh.access_token, newPassword, 'REAUTH_REQUIRED');
  }
}

function jwtEmail(token: string): string | null {
  try {
    const part = token.split('.')[1] ?? '';
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as { email?: unknown };
    return typeof json.email === 'string' ? json.email : null;
  } catch {
    return null;
  }
}

/**
 * Transition mode (VITE_LOCAL_AUTH_FALLBACK=true): Supabase first, local accounts as a
 * fallback (demo accounts, DEV role switcher). The API must allow it too (LOCAL_AUTH_FALLBACK).
 */
export class HybridAuthClient implements AuthClient {
  readonly kind = 'supabase' as const;
  constructor(
    private readonly supabase: SupabaseAuthClient,
    private readonly local: LocalAuthClient,
  ) {}

  async getToken() {
    return (await this.local.getToken()) ?? this.supabase.getToken();
  }

  async login(email: string, password: string) {
    try {
      await this.supabase.login(email, password);
      await this.local.logout();
    } catch {
      await this.local.login(email, password);
    }
  }

  async register(input: { fullName: string; email: string; password: string; phone?: string; language: 'fr' | 'en' }) {
    await this.local.logout();
    await this.supabase.register(input);
  }

  async devLogin(email: string) {
    await this.supabase.logout();
    await this.local.devLogin(email);
  }

  async logout() {
    await this.local.logout();
    await this.supabase.logout();
  }

  /** The account may live at Supabase or locally (demo, not yet migrated): ask both, same neutral answer. */
  async requestPasswordReset(email: string) {
    const results = await Promise.allSettled([this.supabase.requestPasswordReset(email), this.local.requestPasswordReset(email)]);
    if (results.every((r) => r.status === 'rejected')) throw (results[0] as PromiseRejectedResult).reason;
  }

  resetMode(search: URLSearchParams) {
    return this.local.resetMode(search) ?? this.supabase.resetMode();
  }

  async resetPassword(input: { password: string; token?: string | null }) {
    if (input.token) return this.local.resetPassword(input);
    return this.supabase.resetPassword(input);
  }

  /** Decided by the session the user is logged in with. */
  async changePassword(currentPassword: string, newPassword: string) {
    if (await this.local.getToken()) return this.local.changePassword(currentPassword, newPassword);
    return this.supabase.changePassword(currentPassword, newPassword);
  }
}

export function createAuthClient(): AuthClient {
  const env = import.meta.env;
  if (env.VITE_AUTH_PROVIDER === 'supabase' && env.VITE_SUPABASE_URL && env.VITE_SUPABASE_ANON_KEY) {
    const supabase = new SupabaseAuthClient(env.VITE_SUPABASE_URL, env.VITE_SUPABASE_ANON_KEY);
    return env.VITE_LOCAL_AUTH_FALLBACK === 'true' ? new HybridAuthClient(supabase, new LocalAuthClient()) : supabase;
  }
  return new LocalAuthClient();
}
